/**
 * PWA update flow — no user should ever be a version behind without knowing.
 *
 * History: with `registerType: 'autoUpdate'`, every deploy left users one
 * load behind — the old service worker serves the page it precached, the new
 * worker installs silently in the background, and only the NEXT visit gets
 * fresh code. Three separate testing sessions tripped over this ("I don't
 * see the new feature") and the workaround was a manual hard refresh.
 *
 * Now: `registerType: 'prompt'`. When a new version is waiting we surface a
 * small toast ("ZoneIn was updated — reload when ready"); the user taps and
 * the new worker takes over and reloads. Deliberately NOT auto-reloading:
 * a surprise reload mid-brain-dump would destroy typed input — the user
 * chooses the moment. Long-lived tabs poll for updates hourly so the toast
 * appears without needing a fresh visit.
 */

import { registerSW } from 'virtual:pwa-register';
import { isNative } from '../native/native';

type Listener = (ready: boolean) => void;

let updateReady = false;
let applyFn: (() => void) | null = null;
let swRegistration: ServiceWorkerRegistration | undefined;
const listeners = new Set<Listener>();

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly

/**
 * NATIVE SHELL: never register the PWA service worker inside the Capacitor
 * WebView. Android upgrades keep Cache Storage and SW registrations, so a
 * Workbox worker precached from build N keeps serving build N's bundle
 * after Play has installed build N+1 (seen as "the app still shows the old
 * version"; the same class of failure was the build-18 billing stall of
 * 27 Sept 2026, where a nested dynamic chunk never arrived). Updates on
 * native come from Google Play alone. Any worker a previous build left
 * behind is unregistered and its Workbox caches deleted, once, here.
 */
async function retireNativeServiceWorker(): Promise<void> {
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((k) => k.startsWith('workbox-')).map((k) => caches.delete(k)));
    }
  } catch {
    /* best effort — a failure here must never block boot */
  }
}

/**
 * The firebaseapp.com origin hosts Firebase's own pages — the Google sign-in
 * popup (/__/auth/handler, Firebase authDomain) and the native sign-in
 * handoff (/link.html) — and MUST stay worker-free (CLAUDE.md §3).
 * 13 Oct 2026 (MEASURED): opening the app's root on that origin installed
 * the PWA worker there; it then served the app at /__/auth/handler and every
 * web Google sign-in looped back to the start screen. The app is served from
 * web.app; on firebaseapp.com it never registers and retires any worker a
 * previous visit left behind.
 */
export function isWorkerFreeOrigin(hostname: string): boolean {
  return hostname.endsWith('.firebaseapp.com');
}

/**
 * VERSION CHECK — the fallback that does not depend on the service worker
 * (15 Oct 2026, MEASURED twice in the founder's Chrome after deploys
 * 77b2456 and aafd1be): the app opened on the PREVIOUS bundle, no worker was
 * waiting, and no toast appeared — the new version showed only after a
 * manual reload. A local two-build reproduction showed the toast working as
 * designed, so the live cause is not yet known (CLAUDE.md §2: instrument
 * before iterating). Whatever the worker does, the app now compares the
 * bundle it is RUNNING with the one the server's index.html names, and
 * offers the reload when they differ. Every step is written to a small
 * on-device log (UPDATE_LOG_KEY) so the next live miss diagnoses itself.
 */
export const UPDATE_LOG_KEY = 'zonein-update-log';
const UPDATE_LOG_MAX = 30;
const VERSION_CHECK_MIN_GAP_MS = 60 * 1000;
let lastVersionCheck = 0;

/** The entry bundle an index.html loads (`/assets/index-XXXX.js`), or null. */
export function entryBundle(html: string): string | null {
  for (const tag of html.match(/<script\b[^>]*>/g) ?? []) {
    if (!/type=["']module["']/.test(tag)) continue;
    const m = tag.match(/src=["']([^"']*\/assets\/index-[^"'/]+\.js)["']/);
    if (m) return m[1].slice(m[1].lastIndexOf('/') + 1);
  }
  return null;
}

/** True only when both are known and differ — never on a failed read. */
export function isNewerBundle(running: string | null, onServer: string | null): boolean {
  return Boolean(running && onServer && running !== onServer);
}

export function logUpdateEvent(event: string, detail: Record<string, unknown> = {}): void {
  try {
    const raw = localStorage.getItem(UPDATE_LOG_KEY);
    const log = (raw ? (JSON.parse(raw) as unknown[]) : []).slice(-(UPDATE_LOG_MAX - 1));
    log.push({ at: new Date().toISOString(), event, ...detail });
    localStorage.setItem(UPDATE_LOG_KEY, JSON.stringify(log));
  } catch {
    /* diagnostics only — never let logging break the app */
  }
}

function markUpdateReady(reason: string): void {
  if (updateReady) return;
  updateReady = true;
  logUpdateEvent('toast-shown', { reason });
  listeners.forEach((l) => l(true));
}

async function checkServerVersion(trigger: string): Promise<void> {
  const now = Date.now();
  if (now - lastVersionCheck < VERSION_CHECK_MIN_GAP_MS) return;
  lastVersionCheck = now;
  const running = entryBundle(document.documentElement.outerHTML);
  try {
    // A plain fetch (not a navigation) with a unique query: the worker's
    // precache does not match it and navigateFallback does not apply, so this
    // reaches the network.
    const res = await fetch(`/index.html?version-check=${now}`, { cache: 'no-store' });
    const onServer = res.ok ? entryBundle(await res.text()) : null;
    const newer = isNewerBundle(running, onServer);
    logUpdateEvent('version-check', {
      trigger,
      running,
      onServer,
      newer,
      waiting: Boolean(swRegistration?.waiting),
    });
    if (newer) markUpdateReady('server has a newer bundle');
  } catch (e) {
    logUpdateEvent('version-check-failed', { trigger, running, error: String(e) });
  }
}

export function initPwaUpdates(): void {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  if (isNative() || isWorkerFreeOrigin(window.location.hostname)) {
    void retireNativeServiceWorker();
    return;
  }
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // updateSW(true) tells the waiting worker to take over, then reloads.
      applyFn = () => void updateSW(true);
      logUpdateEvent('need-refresh');
      markUpdateReady('worker waiting');
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      swRegistration = registration;
      logUpdateEvent('registered', {
        running: entryBundle(document.documentElement.outerHTML),
        controlled: Boolean(navigator.serviceWorker.controller),
        waiting: Boolean(registration.waiting),
      });
      setInterval(() => {
        registration.update().catch(() => {
          /* offline or transient — next interval retries */
        });
        void checkServerVersion('hourly');
      }, UPDATE_CHECK_INTERVAL_MS);
    },
  });
  // The fallback: a few seconds after load (let the worker settle first), and
  // whenever the app comes back to the screen.
  setTimeout(() => void checkServerVersion('load'), 4000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkServerVersion('visible');
  });
}

/** Subscribe to update-ready state; fires immediately with current state. */
export function subscribeUpdate(l: Listener): () => void {
  listeners.add(l);
  l(updateReady);
  return () => {
    listeners.delete(l);
  };
}

export function isUpdateReady(): boolean {
  return updateReady;
}

/**
 * Activate the waiting service worker and reload with the new version.
 * Belt AND braces (a Reload button that does nothing destroys trust):
 * 1. the library path (message the waiting worker, reload on takeover);
 * 2. a direct SKIP_WAITING message to the waiting worker ourselves;
 * 3. a hard fallback — if nothing has reloaded the page within 1.5s,
 *    reload it anyway; the activated worker then serves the new version.
 */
export function applyUpdate(): void {
  logUpdateEvent('reload-tapped', { waiting: Boolean(swRegistration?.waiting) });
  try {
    swRegistration?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    navigator.serviceWorker?.addEventListener(
      'controllerchange',
      () => window.location.reload(),
      { once: true },
    );
  } catch {
    /* fall through to the library path + timeout */
  }
  applyFn?.();
  setTimeout(() => window.location.reload(), 1500);
}
